import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { AssignTeamModal } from "./AssignTeamModal";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Tooltip from "@mui/material/Tooltip";

import React, { memo, useContext, useEffect, useState } from "react";
import { formatTimeFromUtc } from "utils/formatters";
import { getRouteLink, getYearsFromUtc } from "utils/general";
import { useHistory } from "react-router-dom";
import { PAGES } from "constants/pages";
import { MultiSelect } from "components/SportSelect/MultiSelect";
import { updatePatient } from "store/PatientStore/actions";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import {
  selectAthleteIdInProcess,
  selectPatientSportsUploading,
} from "store/PatientStore/selectors";
import { selectCurrentClinician } from "store/ClinicianStore/selectors";
import { ScoreDelta } from "components/ScoreDelta/ScoreDelta";
import { AddCoachToAthleteModal } from "pages/PatientOverview/components/Athlete/AddCoachModal";
import { StatusBall } from "../../../../components/StatusBall/StatusBall";
import { StatusENUM } from "../../../../types/common";
import { getDates } from "../../../../utils/helpers";
import { USER_GROUPS } from "../../../../constants/roles";
import { PatientRepository } from "../../../../repositories/PatientRepository";
import { AuthRepository } from "../../../../repositories/AuthRepository";
import { AuthContext } from "../../../../contexts/authContext";
import ConfirmModal from "../../../../components/ConfirmModal/ConfirmModal";
import { icons } from "constants/icons";
import { AssignSportModal } from "./AssignSportModal";

type Props = {
  user_id: string;
  status: string;
  isFreeForm?: boolean;
  firstName: string;
  patientImage: string;
  email: string;
  authStatus?: StatusENUM;
  lastName: string;
  inviteDates?: string[];
  dateOfBirth: string;
  lastExam?: string | null;
  leftSD?: {
    value?: number | null;
    isBetter?: boolean;
  };
  rightSD?: {
    value?: number | null;
    isBetter?: boolean;
  };
  bothSD?: [
    {
      leftValue?: number | null;
      leftIsBetter?: boolean;
    },
    {
      rightValue?: number | null;
      rightIsBetter?: boolean;
    }
  ];
  userSports?: string[];
  teams?: string[];
  teamList?: string[];
  position?: string[];
  currentAverage?: number;
};

export const AthleteTableRow = memo(
  ({
    user_id,
    teamList = [],
    currentAverage,
    firstName,
    isFreeForm,
    patientImage,
    lastName,
    dateOfBirth,
    leftSD,
    rightSD,
    bothSD,
    lastExam,
    status,
    userSports,
    email,
    authStatus,
    inviteDates,
    teams = [],
    position,
  }: Props) => {
    const authContext = useContext(AuthContext);
    const [imgAlt, setImgAlt] = useState("");
    const [authInfo, setAuthInfo] = useState({ authStatus, inviteDates });
    const [isEditButtonDarkMode, setIsEditButtonDarkMode] = useState(false);
    const currentClinician: any = useAppSelector(selectCurrentClinician);
    const [style, setStyle] = useState({});
    const [isCoachModalOpen, setIsCoachModalOpen] = useState(false);
    const [tags, setTags] = useState<string[]>([]);
    const history = useHistory();
    const dispatch = useAppDispatch();
    const athleteSportsUpdating = useAppSelector(selectPatientSportsUploading);
    const athleteIdInProcess = useAppSelector(selectAthleteIdInProcess);
    const [loading, setLoading] = useState(false);
    const [userStatus, setUserStatus] = React.useState<string>(status);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
    const [openTeamModal, setOpenTeamModal] = React.useState(false);
    const [openSportModal, setOpenSportModal] = React.useState(false);
    const [currentTeams, setCurrentTeams] = React.useState(teams);
    const [currentSports, setCurrentSports] = React.useState(userSports);
    const parentButtonHandler = (e: any) => {
      e.stopPropagation();
      const url = isFreeForm
        ? `${getRouteLink(PAGES.patientDetails)}${user_id}?freeForm=true`
        : `${getRouteLink(PAGES.patientDetails)}${user_id}`;
      history.push(url);
    };
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const dotsMenuOpen = Boolean(anchorEl);

    const addToTags = (value: string) => {
      setTags([...tags, value]);
    };

    const removeFromTags = (value: string) => {
      setTags(tags.filter((item) => item !== value));
    };

    const onItemSelected = (e: any, name: string) => {
      if (e.target.checked) {
        addToTags(name);
      } else {
        removeFromTags(name);
      }
    };

    useEffect(() => {
      if (firstName?.length > 0 && lastName?.length > 0) {
        setImgAlt(firstName[0] + lastName[0]);
      } else if (firstName?.length > 0) {
        setImgAlt(firstName[0]);
      } else if (lastName?.length > 0) {
        setImgAlt(lastName[0]);
      }
      if (!patientImage) {
        setStyle({ textAlign: "center", paddingTop: "2px", color: "white" });
      }
      /*   if (userSports && userSports.length > 0) {
        userSports.forEach((e) => {
          addToTags(e);
        });
      } */
      return () => {
        setImgAlt("");
        setStyle({});
      };
    }, [firstName, lastName]);

    const handleClickOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
      setIsEditButtonDarkMode(false);
    };
    const handleClose = (e?: any) => {
      e.stopPropagation();
      setAnchorEl(null);
      setIsEditButtonDarkMode(false);
    };
    const handleReInvite = async (e: any) => {
      e?.stopPropagation();
      try {
        setLoading(true);
        await authContext.reSendClinicInvite(email, USER_GROUPS.ATHLETE);

        const patientRepo = PatientRepository.getInstance();
        const dates = authInfo.inviteDates ?? [];
        const newInviteDates = [
          ...dates,
          new Date().toISOString().slice(0, 10),
        ];
        await patientRepo.updatePatient(user_id, {
          inviteDates: newInviteDates,
          authStatus: "invited",
        });

        setAuthInfo({
          inviteDates: newInviteDates,
          authStatus: StatusENUM.invited,
        });
        handleClose(e);
        enqueueSnackbar("Invite successfull", { variant: "success" });

        setLoading(false);
      } catch (err: any) {
        enqueueSnackbar(err.message, { variant: "error" });

        setLoading(false);
      }
    };

    const handleOpenConfirmModal = (e: any) => {
      e.stopPropagation();
      setIsConfirmModalOpen(true);
    };

    const handleUpdateStatus = async () => {
      const authRepo = AuthRepository.getInstance();

      try {
        setLoading(true);
        if (userStatus === StatusENUM.disabled) {
          await authRepo.enableAthlete(user_id);
          setUserStatus(StatusENUM.active);
        } else {
          await authRepo.disableAthlete(user_id);
          setUserStatus(StatusENUM.disabled);
        }

        setLoading(false);
        setIsConfirmModalOpen(false);
      } catch (err) {
        setLoading(false);
      }
      handleClose();
      setIsCoachModalOpen(false);
    };

    const options =
      authStatus !== StatusENUM.registered
        ? [
            {
              text: "Assign team",
              handler: (e: any) => {
                e.stopPropagation();
                setOpenTeamModal(true);
                setAnchorEl(null);
              },
            },
            {
              text: "Assign sports",
              handler: (e: any) => {
                e.stopPropagation();
                setOpenSportModal(true);
                setAnchorEl(null);
              },
            },
            {
              text: "Assign Coach",
              handler: (e: any) => {
                e.stopPropagation();
                setIsCoachModalOpen(true);
                setAnchorEl(null);
              },
            },
            {
              text: "Send another invite",
              handler: handleReInvite,
            },
          ]
        : [
            {
              text: "Assign team",
              handler: (e: any) => {
                e.stopPropagation();
                setOpenTeamModal(true);
                setAnchorEl(null);
              },
            },
            {
              text: "Assign sports",
              handler: (e: any) => {
                e.stopPropagation();
                setOpenSportModal(true);
                setAnchorEl(null);
              },
            },
            {
              text: "Assign Coach",
              handler: (e: any) => {
                e.stopPropagation();
                setIsCoachModalOpen(true);
                setAnchorEl(null);
              },
            },
          ];

    const { parsedDates: dates, lastInviteOlderThat1Week } = getDates(
      authInfo.inviteDates
    );

    const isInviteExpired =
      lastInviteOlderThat1Week && authInfo.authStatus === "invited";

    const getStatus = (capitalize?: boolean, isDisabled?: boolean) => {
      const statusString = authInfo.authStatus ?? "";
      if (!statusString) {
        return "";
      }

      if (isDisabled) {
        return "Disabled";
      }

      if (statusString === StatusENUM.invited) {
        if (isInviteExpired) {
          return icons.coachExpiredStatus;
        }

        return icons.coachInvitedStatus;
      }

      if (statusString === StatusENUM.registered) {
        return icons.coachRegisteredStatus;
      }

      if (statusString === StatusENUM.expired) {
        return icons.coachExpiredStatus;
      }
    };

    if (authInfo.authStatus === StatusENUM.registered) {
      options.push({
        text:
          userStatus === StatusENUM.disabled
            ? "Activate user"
            : "Deactivate user",
        handler: handleOpenConfirmModal,
      });
    }

    const confirmModalContent = React.useMemo(() => {
      if (userStatus === StatusENUM.disabled) {
        return "Do you want to reactivate this user?";
      }

      return "Are you sure you want to disable this user?";
    }, [userStatus]);
    const onConfirmClose = () => {
      setIsConfirmModalOpen(false);
    };

    const getHighlightedStyle = (
      left: any,
      right: any,
      currentSide: string
    ) => {
      if (!left || !right) {
        return {};
      }
      const gap = Math.abs(Number(left) - Number(right));

      if (gap < 4) {
        return {};
      }
      let moreNegative;
      if (Number(left) < Number(right)) {
        moreNegative = "left";
      } else if (Number(right) < Number(left)) {
        moreNegative = "right";
      }

      console.log({
        left: Number(left),
        right: Number(right),
        currentSide,
        moreNegative,
      });
      if (moreNegative === currentSide) {
        return { color: "red" };
      }
    };
    return (
      <>
        <div
          className="group w-full min-h-[54px] card rounded-xl flex items-center font-normal text-grayDark hover:bg-bgDark hover:text-white hover:shadow-xl px-2"
          onClick={parentButtonHandler}
          aria-hidden="true"
          onMouseEnter={() => setIsEditButtonDarkMode(true)}
          onMouseLeave={() => setIsEditButtonDarkMode(false)}
        >
          <div className="w-[30px]"></div>
          <div
            className={`flex items-center space-x-3 truncate-long-texts ${
              isFreeForm ? "w-[335px]" : "w-[280px]"
            }`}
          >
            <div className="w-[30px] h-[30px] bg-grayDark rounded-full overflow-hidden">
              {patientImage ? (
                <img
                  className="object-cover min-w-full min-h-full"
                  src={patientImage || ""}
                  alt={imgAlt}
                />
              ) : (
                <p style={style}>{imgAlt}</p>
              )}
            </div>
            <p>
              {firstName} {lastName}
            </p>
          </div>
          <div className={`px-2 ${isFreeForm ? "w-[103px]" : "w-[90px]"}`}>
            {dateOfBirth ? <p>{getYearsFromUtc(dateOfBirth)}</p> : <p>-</p>}
          </div>
          <div className={`px-2 ${isFreeForm ? "w-[150px]" : "w-[120px]"}`}>
            {lastExam ? <p>{formatTimeFromUtc(lastExam)}</p> : <p>-</p>}
          </div>
          {!isFreeForm && (
            <div
              className="px-3 mx-2"
              style={{
                display: "flex",
                justifyContent: "center",
                backgroundColor: "rgb(255 181 0)",
                borderRadius: "12px",
                width: "fit-content",
                minWidth: "80px",
              }}
            >
              {currentAverage ? <p>{currentAverage}</p> : <p>-</p>}
            </div>
          )}

          {leftSD && (
            <div className="w-[120px] px-2">
              <div className="flex space-x-1 items-baseline">
                <p
                  style={getHighlightedStyle(
                    leftSD?.value?.toFixed(),
                    rightSD?.value?.toFixed(),
                    "left"
                  )}
                  title="Left Minimum Value"
                >
                  {leftSD?.value?.toFixed(2) ?? "-"}
                </p>
                {leftSD?.value && <ScoreDelta isBetter={leftSD?.isBetter} />}
              </div>
            </div>
          )}
          {rightSD && (
            <div className="w-[120px] px-2">
              <div className="flex space-x-1 items-baseline">
                <p
                  style={getHighlightedStyle(
                    leftSD?.value?.toFixed(),
                    rightSD?.value?.toFixed(),
                    "right"
                  )}
                  title="Right Minimum Value"
                >
                  {rightSD?.value?.toFixed(2) ?? "-"}
                </p>
                {rightSD?.value && <ScoreDelta isBetter={rightSD?.isBetter} />}
              </div>
            </div>
          )}
          {bothSD && (
            <div className="w-[220px] px-2">
              <div className="flex space-x-1 items-baseline">
                {bothSD?.[0].leftValue || bothSD?.[1].rightValue ? (
                  <>
                    <p title="Left Minimum Value">
                      {bothSD?.[0].leftValue?.toFixed(2)}
                    </p>
                    {bothSD?.[0].leftValue && (
                      <ScoreDelta isBetter={bothSD?.[0].leftIsBetter} />
                    )}
                    <p title="Right Minimum Value">
                      {bothSD?.[1].rightValue?.toFixed(2)}
                    </p>
                    {bothSD?.[1].rightValue && (
                      <ScoreDelta isBetter={bothSD?.[1].rightIsBetter} />
                    )}
                  </>
                ) : (
                  <p>-</p>
                )}
              </div>
            </div>
          )}
          {currentSports && (
            <div className={`px-2 ${isFreeForm ? "w-[280px]" : "w-[220px]"}`}>
              <Tooltip
                placement="top"
                title={
                  <Grid
                    sx={{
                      background: "#fff",
                      padding: "10px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Grid style={{ background: "#fff", color: "#000" }}>
                      {currentSports &&
                        currentSports.length > 0 &&
                        currentSports.map((sport) => <Grid>{sport}</Grid>)}
                    </Grid>
                  </Grid>
                }
              >
                <Grid
                  md={10}
                  xl={10}
                  sx={{
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  {currentSports &&
                    currentSports.length > 0 &&
                    currentSports[0]}
                </Grid>
              </Tooltip>
            </div>
          )}
          <div
            className={`capitalize px-2 ${
              isFreeForm ? "w-[180px]" : "w-[170px]"
            }`}
          >
            <Tooltip
              placement="top"
              title={
                <Grid sx={{ background: "#fff", padding: "10px" }}>
                  <p
                    style={{
                      background: "#fff",
                      color: "#000",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div>
                      {teamList
                        .filter((team: any) => currentTeams.includes(team._id))
                        .map((team: any) => (
                          <p key={team.name}>{team.name}</p>
                        ))}
                    </div>
                  </p>
                </Grid>
              }
            >
              <Grid
                sx={{
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {teamList
                  .filter((team: any) => currentTeams.includes(team._id))
                  .map((team: any, i: any) => {
                    if (i !== 0) {
                      return <></>;
                    }
                    return <p key={team.name}>{team.name}</p>;
                  })}
              </Grid>
            </Tooltip>
          </div>
          <div
            className={`capitalize px-2 ${
              isFreeForm ? "w-[280px]" : "w-[170px]"
            }`}
          >
            {position && (
              <div className={isFreeForm ? "w-[280px]" : "w-[200px]"}>
                <Tooltip
                  hidden={position?.length === 0}
                  placement="top"
                  title={
                    <Grid
                      sx={{
                        background: "#fff",
                        padding: "10px",
                        color: "#000",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {typeof position == "string" ? (
                        position
                      ) : position?.length > 0 ? (
                        position.map((item) => <p>{item}</p>)
                      ) : (
                        <></>
                      )}
                    </Grid>
                  }
                >
                  <div className="w-[200px]">
                    <Grid
                      md={10}
                      xl={10}
                      sx={{
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {typeof position == "string" ? (
                        position
                      ) : position?.length > 0 ? (
                        position[0]
                      ) : (
                        <></>
                      )}
                    </Grid>
                  </div>
                </Tooltip>
              </div>
            )}
          </div>
          <div
            className={`capitalize px-2 flex items-center ${
              isFreeForm ? "w-[150px]" : "w-[80px]"
            }`}
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            {userStatus === "disabled" && (
              <img
                alt="status"
                style={{ marginRight: "2px" }}
                src={icons.clinicDisabledStatus}
              />
            )}
            {authInfo.authStatus && userStatus !== "disabled" && (
              <img
                // @ts-ignore
                src={getStatus()}
              />
            )}
          </div>
          <div className={`px-2 ${isFreeForm ? "w-[100px]" : "w-[60px]"}`}>
            {typeof currentSports !== "undefined" ? (
              <Grid
                md={10}
                xl={10}
                container
                justifyContent="flex-start"
                alignItems="center"
              >
                <Grid md={2} xl={2} sx={{ zIndex: 1000 }}>
                  {athleteSportsUpdating && athleteIdInProcess == user_id && (
                    <Box sx={{ display: "flex" }}>
                      <CircularProgress size={20} />
                    </Box>
                  )}
                  {athleteIdInProcess !== user_id && (
                    <Box sx={{ display: "flex" }}>
                      <IconButton
                        aria-label="more"
                        id="long-button"
                        aria-controls={dotsMenuOpen ? "long-menu" : undefined}
                        aria-expanded={dotsMenuOpen ? "true" : undefined}
                        aria-haspopup="true"
                        sx={{ pl: 0, mt: "-3px", cursor: "pointer" }}
                        onClick={handleClickOpenMenu}
                      >
                        <MoreVertIcon
                          sx={isEditButtonDarkMode ? { color: "white" } : {}}
                        />
                      </IconButton>

                      <Menu
                        id="long-menu"
                        MenuListProps={{
                          "aria-labelledby": "long-button",
                        }}
                        anchorEl={anchorEl}
                        open={dotsMenuOpen}
                        onClose={handleClose}
                        PaperProps={{
                          style: loading
                            ? {
                                maxHeight: 48 * 4.5,
                                width: "20ch",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }
                            : {
                                maxHeight: 48 * 4.5,
                                width: "20ch",
                              },
                        }}
                      >
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : (
                          options.map((option) => (
                            <MenuItem
                              style={{ paddingLeft: "15px" }}
                              key={option.text}
                              onClick={(e) => {
                                option.handler(e);
                              }}
                            >
                              {option.text}
                            </MenuItem>
                          ))
                        )}
                      </Menu>
                    </Box>
                  )}
                </Grid>
              </Grid>
            ) : (
              <p>{!isFreeForm && "---"}</p>
            )}
            {isFreeForm && (
              <>
                <IconButton
                  aria-label="more"
                  id="long-button"
                  aria-controls={dotsMenuOpen ? "long-menu" : undefined}
                  aria-expanded={dotsMenuOpen ? "true" : undefined}
                  aria-haspopup="true"
                  sx={{ pl: 0, mt: "-3px", cursor: "pointer" }}
                  onClick={handleClickOpenMenu}
                >
                  <MoreVertIcon
                    sx={isEditButtonDarkMode ? { color: "white" } : {}}
                  />
                </IconButton>

                <Menu
                  id="long-menu"
                  MenuListProps={{
                    "aria-labelledby": "long-button",
                  }}
                  anchorEl={anchorEl}
                  open={dotsMenuOpen}
                  onClose={handleClose}
                  PaperProps={{
                    style: {
                      maxHeight: 48 * 4.5,
                      width: "20ch",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : (
                    options.map((option) => (
                      <MenuItem
                        style={{ paddingLeft: "15px" }}
                        key={option.text}
                        onClick={(e) => {
                          option.handler(e);
                        }}
                      >
                        {option.text}
                      </MenuItem>
                    ))
                  )}
                </Menu>
              </>
            )}
          </div>
          {isCoachModalOpen && (
            <AddCoachToAthleteModal
              firstName={firstName}
              lastName={lastName}
              isEnabled={isCoachModalOpen}
              patientId={user_id}
              setIsEnabled={setIsCoachModalOpen}
            />
          )}
          {openSportModal && (
            <AssignSportModal
              sportsList={currentClinician.profileInfo.sports}
              patientSports={currentSports || []}
              isEnabled={openSportModal}
              patientId={user_id}
              setCurrentSports={setCurrentSports}
              setIsEnabled={setOpenSportModal}
            />
          )}
          {openTeamModal && (
            <AssignTeamModal
              teamList={teamList}
              patientTeams={currentTeams}
              isEnabled={openTeamModal}
              patientId={user_id}
              setCurrentTeams={setCurrentTeams}
              setIsEnabled={setOpenTeamModal}
            />
          )}
        </div>
        <ConfirmModal
          title={`${
            userStatus === StatusENUM.disabled ? "Reactivate" : "Disable"
          } ${"User"} `}
          open={isConfirmModalOpen}
          onAccept={handleUpdateStatus}
          onClose={onConfirmClose}
          content={confirmModalContent}
          acceptButtonText="Yes"
          cancelButtonText="No"
        />
      </>
    );
  }
);
